import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, FileText, Calendar, Shield, Scale, ChevronDown, MessageSquare, PenTool, Zap, Gavel } from 'lucide-react';
import { Mail, Phone, MapPin, ExternalLink, Menu, X } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

// Import images from src/images
import img01 from '../images/1.jpeg';
import img02 from '../images/2.jpeg';
import img03 from '../images/3.jpeg';
import img04 from '../images/4.jpeg';
import img011 from '../images/011.png';
import img012 from '../images/012.png';
import img013 from '../images/013.png';
import leftSvg from '../images/left.svg';
import rightSvg from '../images/right.svg';
import stSvg from '../images/st.svg';
// import hero1270 from '../images/1366x768.png';
import hero1270 from '../images/hero.png';
// import hero1024 from '../images/1366x768.png';
import hero1024 from '../images/hero.png';
import hero768 from '../images/hero-2.png';
import hero400 from '../images/hero-3.png';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const [showUrgentMenu, setShowUrgentMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const urgentMenuRef = useRef(null);

  // Monitor scroll for header background transitions
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (urgentMenuRef.current && !urgentMenuRef.current.contains(event.target)) {
        setShowUrgentMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVirtualCourtClick = () => {
    if (window.location.hostname.includes('hklaw.sa') || window.location.hostname.includes('localhost')) {
      window.open('https://hukm.cloud', '_blank');
    } else {
      navigate('/virtual-court');
    }
  };

  const services = [
    {
      title: t('landing.commercialCases'),
      desc: t('landing.commercialCasesDesc'),
      image: img02,
      icon: Building2,
      id: 'service-commercial'
    },
    {
      title: t('landing.civilCases'),
      desc: t('landing.civilCasesDesc'),
      image: img01,
      icon: Scale,
      id: 'service-civil'
    },
    {
      title: t('landing.laborCases'),
      desc: t('landing.laborCasesDesc'),
      image: img03,
      icon: Users,
      id: 'service-labor'
    },
    {
      title: t('landing.familyLaw'),
      desc: t('landing.familyLawDesc'),
      image: img04,
      icon: Shield,
      id: 'service-family'
    },
    {
      title: t('landing.realEstate'),
      desc: t('landing.realEstateDesc'),
      image: img011,
      icon: PenTool,
      id: 'service-realestate'
    },
    {
      title: t('landing.legalConsulting'),
      desc: t('landing.legalConsultingDesc'),
      image: img012,
      icon: MessageSquare,
      id: 'service-consulting'
    }
  ];

  return (
    <div className="min-h-screen bg-sand font-body text-primary-900 selection:bg-gold/30 selection:text-gold-dark" dir={dir}>
      {/* Premium Header */}
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-primary/95 backdrop-blur-md shadow-lg border-b border-gold/20 py-3'
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          {/* Logo & Brand Title */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Logo size="md" />
            <div className="h-7 w-[1px] bg-white/20 hidden md:block"></div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-white tracking-wide leading-none hidden sm:block">
              {t('landing.heroTitle')}
            </h1>
          </div>

          {/* Desktop Controls */}
          <div className="hidden lg:flex items-center gap-5">
            <LanguageSelector variant="minimal" className="text-white hover:text-gold transition-colors font-medium" />
            
            <button
              data-testid="header-download-btn"
              onClick={() => navigate('/download')}
              className="px-4 py-2 text-white border border-gold/45 rounded-lg hover:bg-gold/10 hover:border-gold hover:text-gold transition-all duration-300 flex items-center gap-2 text-sm font-semibold"
              title="حمّل التطبيق"
            >
              <svg className="w-4.5 h-4.5 text-gold animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>التطبيق</span>
            </button>

            <button
              data-testid="header-login-btn"
              onClick={() => navigate('/login')}
              className="px-5 py-2 text-white/90 font-medium hover:text-gold transition-colors duration-300"
            >
              {t('nav.login')}
            </button>

            <button
              data-testid="header-register-btn"
              onClick={() => navigate('/register')}
              className="px-6 py-2.5 bg-gold text-white font-semibold rounded-lg hover:bg-gold-dark hover:scale-105 active:scale-95 transition-all duration-300 shadow-md hover:shadow-gold/25"
            >
              {t('nav.register')}
            </button>
          </div>

          {/* Mobile Navigation Toggle */}
          <div className="flex lg:hidden items-center gap-3">
            <LanguageSelector variant="minimal" className="text-white" />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-primary/98 backdrop-blur-xl border-b border-gold/20 p-6 flex flex-col gap-4 shadow-2xl animate-in fade-in slide-in-from-top-5 duration-300">
            <h2 className="text-sm font-semibold text-gold tracking-widest text-center uppercase pb-2 border-b border-white/10">
              {t('landing.heroTitle')}
            </h2>
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/download'); }}
              className="w-full py-3.5 text-white border border-gold/45 rounded-lg hover:bg-gold/15 transition-all duration-300 flex justify-center items-center gap-2 font-semibold"
            >
              <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>حمّل التطبيق</span>
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
              className="w-full py-3.5 text-white border border-white/15 rounded-lg hover:bg-white/5 transition-all duration-300 font-semibold"
            >
              {t('nav.login')}
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/register'); }}
              className="w-full py-3.5 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all duration-300 font-semibold shadow-lg shadow-gold/20"
            >
              {t('nav.register')}
            </button>
          </div>
        )}
      </header>

      {/* Immersive Hero Section */}
      <section className="relative min-h-[92vh] w-full flex items-center justify-center overflow-hidden pt-20">
        {/* Background Responsive Image */}
        <picture className="absolute inset-0 w-full h-full overflow-hidden z-0">
          <source media="(min-width: 1270px)" srcSet={hero1270} />
          <source media="(min-width: 1024px)" srcSet={hero1024} />
          <source media="(min-width: 768px)" srcSet={hero768} />
          <img
            src={hero400}
            alt="HK Law Office Banner"
            className="w-full h-full object-cover object-center"
          />
        </picture>
      </section>

      {/* Decorative Wave Separator */}
      <div className="w-full h-auto bg-primary relative -mt-2 z-20 pointer-events-none select-none overflow-hidden">
        <img src={stSvg} alt="divider" className="w-full h-full object-cover" />
      </div>

      {/* Legal Services Section */}
      <section className="py-24 bg-primary relative overflow-hidden">
        {/* Background SVGs as clean watermarks */}
        <img src={leftSvg} alt="left-decoration" className="absolute top-1/4 left-0 w-auto h-[35%] opacity-[0.03] z-0 pointer-events-none" />
        <img src={rightSvg} alt="right-decoration" className="absolute bottom-1/4 right-0 w-auto h-[35%] opacity-[0.03] z-0 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-accent font-bold text-white mb-3">
              {t('landing.ourServicesTitle')}
            </h3>
            <div className="w-20 h-1 bg-gold mx-auto mb-4 rounded-full"></div>
            <h4 className="text-sm sm:text-base font-heading text-gold-dark tracking-widest font-semibold uppercase">
              PRACTICE AREAS
            </h4>
          </div>

          {/* Premium Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {services.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  data-testid={item.id}
                  className="group relative h-full bg-white rounded-2xl p-8 border border-white/5 text-center shadow-[0_10px_30px_rgba(0,0,0,0.4)] hover:shadow-[0_20px_60px_rgba(184,134,11,0.15)] hover:-translate-y-1 transition-all duration-350 cursor-pointer overflow-hidden"
                >
                  {/* تأثير إضاءة الخلفية الناعم عند الهوفر */}
                  <div className="absolute inset-0 bg-gradient-to-b from-gold/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-350 pointer-events-none" />

                  {/* حدود داخلية مضيئة تظهر عند الهوفر */}
                  <div className="absolute inset-0 border border-gold/0 group-hover:border-gold/20 rounded-2xl transition-colors duration-350 pointer-events-none" />

                  {/* حاوية الأيقونة الدائرية والموسطة بنفس مقاس المكون الثاني */}
                  <div className="w-16 h-16 bg-gold/10 text-gold rounded-full border-2 border-gold/20 flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-7 h-7" strokeWidth={1.5} />
                  </div>

                  {/* العنوان الرئيسي للخدمة */}
                  <h4 className="text-xl font-heading font-semibold text-primary mb-3 group-hover:text-gold transition-colors duration-350">
                    {item.title}
                  </h4>

                  {/* تفاصيل ووصف الخدمة بنفس الحجم */}
                  <p className="leading-relaxed text-gray-600 font-light transition-colors duration-350">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pillars of Excellence (Why Choose Us) */}
      <section className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-accent font-bold text-white mb-3">
              {t('landing.whyChooseUs')}
            </h3>
            <div className="w-20 h-1 bg-gold mx-auto mb-4 rounded-full"></div>
            <h4 className="text-sm font-heading text-gold-dark tracking-widest font-semibold uppercase">
              OUR CORE PRINCIPLES
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Pillar 1 */}
            <div
              data-testid="why-experience"
              className="h-full bg-white rounded-2xl p-8 border border-sand-dark text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-350"
            >
              <div className="w-16 h-16 bg-gold/10 text-gold-dark rounded-full border-2 border-gold/30 flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-heading font-semibold text-primary mb-3">
                {t('landing.experience')}
              </h4>
              <p className="text-sm leading-relaxed text-gray-600 font-light">
                {t('landing.experienceDesc')}
              </p>
            </div>

            {/* Pillar 2 */}
            <div
              data-testid="why-professionalism"
              className="h-full bg-white rounded-2xl p-8 border border-sand-dark text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-350"
            >
              <div className="w-16 h-16 bg-gold/10 text-gold-dark rounded-full border-2 border-gold/30 flex items-center justify-center mx-auto mb-6">
                <Scale className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-heading font-semibold text-primary mb-3">
                {t('landing.professionalTeam')}
              </h4>
              <p className="text-sm leading-relaxed text-gray-600 font-light">
                {t('landing.professionalTeamDesc')}
              </p>
            </div>

            {/* Pillar 3 */}
            <div
              data-testid="why-support"
              className="h-full bg-white rounded-2xl p-8 border border-sand-dark text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-350"
            >
              <div className="w-16 h-16 bg-gold/10 text-gold-dark rounded-full border-2 border-gold/30 flex items-center justify-center mx-auto mb-6">
                <Users className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-heading font-semibold text-primary mb-3">
                {t('landing.clientFirst')}
              </h4>
              <p className="text-sm leading-relaxed text-gray-600 font-light">
                {t('landing.clientFirstDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Minimalism Stats Bar */}
      <section className="py-14 bg-primary text-white border-t border-b border-gold/20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-heading font-bold text-gold mb-1">15+</div>
              <div className="text-xs sm:text-sm text-sand/75">سنة من الخبرة القانونية</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-heading font-bold text-gold mb-1">1000+</div>
              <div className="text-xs sm:text-sm text-sand/75">قضية منجزة بنجاح</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-heading font-bold text-gold mb-1">100%</div>
              <div className="text-xs sm:text-sm text-sand/75">سرية وأمان تام للبيانات</div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium CTA Section */}
      <section className="py-24 bg-primary relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-primary-900 to-primary text-white px-8 py-16 text-center border-2 border-gold/30 shadow-2xl">
            {/* Islamic motif backdrop */}
            <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(#c9a227_1px,transparent_1px)] [background-size:16px_16px]"></div>
            
            <div className="relative z-10 max-w-3xl mx-auto">
              <h3 className="text-3xl md:text-4xl font-accent font-bold text-white mb-4">
                هل تحتاج إلى استشارة أو مساعدة قانونية؟
              </h3>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  data-testid="footer-register-btn"
                  onClick={() => navigate('/register')}
                  className="w-full sm:w-auto px-10 py-3.5 bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-bold rounded-lg transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  إنشاء حساب جديد
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Art Footer */}
      <footer className="bg-primary text-white border-t-2 border-gold relative overflow-hidden">
        {/* Artistic Islamic Pattern Watermark */}
        <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(#c9a227_1px,transparent_1px)] [background-size:16px_16px]"></div>

        <div className="max-w-7xl mx-auto px-4 py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-right">
            
            {/* Col 1: About HK Law */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Logo size="sm" />
                <h3 className="text-xl font-heading font-bold text-gold">مجموعة HK LAW</h3>
              </div>
              <p className="text-sm leading-relaxed text-sand/85 font-light">
                مجموعة هشام بن يوسف الخياط للمحاماة والاستشارات القانونية والتوثيق. نسعى لتقديم حلول قانونية متكاملة ومبتكرة تجمع بين الأصالة والتقنية الرقمية لتلبية احتياجات عملائنا بأعلى جودة.
              </p>
            </div>

            {/* Col 2: Social Media Linkouts */}
            <div className="text-center md:text-right">
              <h4 className="text-lg font-bold text-gold mb-6 border-b border-gold/20 pb-2">وسائل التواصل</h4>
              <p className="text-sm text-sand/80 mb-4 font-light">تابعونا ليصلكم كل جديد في الأنظمة والتشريعات السعودية:</p>
              <div className="flex justify-center md:justify-end gap-5 items-center mt-6">
                <a
                  href="https://x.com/hisham_group"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 bg-white/10 hover:bg-gold rounded-full text-white hover:text-white transition-all duration-300 hover:scale-110"
                  title="X (Twitter)"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/hisham_group"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 bg-white/10 hover:bg-gold rounded-full text-white hover:text-white transition-all duration-300 hover:scale-110"
                  title="Instagram"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@hisham_group"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 bg-white/10 hover:bg-gold rounded-full text-white hover:text-white transition-all duration-300 hover:scale-110"
                  title="TikTok"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.92-1.31 1.83-3.58 2.98-5.83 3.03-2.73.06-5.46-1.16-6.93-3.47-1.47-2.3-1.47-5.38 0-7.68C4.16 10.36 6.89 9.15 9.62 9.2c.02 1.4-.01 2.79 0 4.19-1.25-.09-2.58.45-3.32 1.48-.79 1.09-.79 2.61 0 3.7.75 1.01 2.08 1.55 3.32 1.46 1.44-.09 2.7-.99 3.24-2.33.26-.65.29-1.37.28-2.07.01-5.18-.01-10.36.02-15.54.13-.03.26-.05.4-.07z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Col 3: Direct Connect */}
            <div>
              <h4 className="text-lg text-center md:text-right font-bold text-gold mb-6 border-b border-gold/20 pb-2">تواصل معنا</h4>
              <ul className="space-y-4.5 text-sm text-sand/80">
                <li className="flex justify-center md:justify-end">
                  <a href="mailto:info@hklaw.sa" className="flex items-center gap-2.5 hover:text-gold transition-colors justify-center md:justify-end w-full">
                    <span>info@hklaw.sa</span>
                    <Mail size={16} className="text-gold" />
                  </a>
                </li>
                <li className="flex justify-center md:justify-end">
                  <a href="tel:+966566656096" className="flex items-center gap-2.5 hover:text-gold transition-colors justify-center md:justify-end w-full">
                    <span dir="ltr">+966 56 665 6096</span>
                    <Phone size={16} className="text-gold" />
                  </a>
                </li>
                <li className="flex justify-center md:justify-end">
                  <a href="https://maps.app.goo.gl/GiWcgbKYtGzfYiWe8" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 hover:text-gold transition-colors justify-center md:justify-end w-full">
                    <span>جدة - المملكة العربية السعودية</span>
                    <MapPin size={16} className="text-gold" />
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Copyright Meta */}
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-sand/40 font-light">
              جميع الحقوق محفوظة © {new Date().getFullYear()} مجموعة هشام بن يوسف الخياط للمحاماة والاستشارات القانونية
            </p>
            <div className="flex gap-6 text-xs text-sand/40 font-light">
              <span className="hover:text-gold cursor-pointer transition-colors">سياسة الخصوصية</span>
              <span className="hover:text-gold cursor-pointer transition-colors">شروط الاستخدام</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
